/**
 * Build-time configuration for every third-party host the browser bundle can
 * talk to. Cloud builds keep the historical defaults; `VITE_ONPREM=true`
 * switches every default to "no external egress" so an on-prem build only
 * talks to its own origin unless an operator explicitly opts in.
 *
 * Default third-party URLs are guarded by a literal
 * `import.meta.env.VITE_ONPREM === "true"` check so Vite folds the condition
 * at build time and the host strings are removed from on-prem bundles
 * (verified by `npm run check:egress`).
 */

export type ThirdPartyEnv = Record<string, string | boolean | undefined>;

/**
 * Explicit key-by-key snapshot of the build env. Passing `import.meta.env`
 * as a whole would inline the entire env object into every chunk that uses
 * it, so only the keys we read are referenced.
 */
export const buildEnv: ThirdPartyEnv = {
  VITE_ONPREM: import.meta.env.VITE_ONPREM,
  VITE_GEOCODER_URL: import.meta.env.VITE_GEOCODER_URL,
  VITE_GEOCODER_ENABLED: import.meta.env.VITE_GEOCODER_ENABLED,
  VITE_RASTER_TILE_URL: import.meta.env.VITE_RASTER_TILE_URL,
  VITE_DUCKDB_EXTENSION_REPOSITORY:
    import.meta.env.VITE_DUCKDB_EXTENSION_REPOSITORY,
  VITE_MAPTILER_KEY: import.meta.env.VITE_MAPTILER_KEY,
  VITE_BASEMAP_PROVIDER: import.meta.env.VITE_BASEMAP_PROVIDER,
  VITE_BASEMAP_STYLES_URL: import.meta.env.VITE_BASEMAP_STYLES_URL,
  VITE_POSTHOG_AUTOCAPTURE: import.meta.env.VITE_POSTHOG_AUTOCAPTURE,
  VITE_POSTHOG_SESSION_RECORDING:
    import.meta.env.VITE_POSTHOG_SESSION_RECORDING,
  VITE_POSTHOG_IDENTIFY_EMAIL: import.meta.env.VITE_POSTHOG_IDENTIFY_EMAIL,
};

const DEFAULT_GEOCODER_URL =
  import.meta.env.VITE_ONPREM === "true"
    ? ""
    : "https://nominatim.openstreetmap.org/search";

const DEFAULT_RASTER_TILE_URL =
  import.meta.env.VITE_ONPREM === "true"
    ? ""
    : "https://tile.openstreetmap.org/{z}/{x}/{y}.png";

const readString = (env: ThirdPartyEnv, key: string): string | undefined => {
  const value = env[key];
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

export const readFlag = (
  env: ThirdPartyEnv,
  key: string,
  fallback: boolean
): boolean => {
  const value = readString(env, key)?.toLowerCase();
  if (value === undefined) return fallback;
  if (["true", "1", "yes", "on"].includes(value)) return true;
  if (["false", "0", "no", "off"].includes(value)) return false;
  return fallback;
};

export const isOnPremEnv = (env: ThirdPartyEnv): boolean =>
  readFlag(env, "VITE_ONPREM", false);

// ---------------------------------------------------------------------------
// Geocoding (address search)
// ---------------------------------------------------------------------------

export type GeocoderConfig = {
  enabled: boolean;
  url: string;
};

export function resolveGeocoderConfig(
  env: ThirdPartyEnv,
  defaultUrl: string = DEFAULT_GEOCODER_URL
): GeocoderConfig {
  const url =
    readString(env, "VITE_GEOCODER_URL") ??
    (isOnPremEnv(env) ? "" : defaultUrl);
  const enabled = readFlag(env, "VITE_GEOCODER_ENABLED", true) && url !== "";
  return { enabled, url: enabled ? url : "" };
}

export const geocoderConfig = resolveGeocoderConfig(buildEnv);

// ---------------------------------------------------------------------------
// Raster XYZ tiles used by lightweight preview maps
// ---------------------------------------------------------------------------

export function resolveRasterTileUrl(
  env: ThirdPartyEnv,
  defaultUrl: string = DEFAULT_RASTER_TILE_URL
): string {
  return (
    readString(env, "VITE_RASTER_TILE_URL") ??
    (isOnPremEnv(env) ? "" : defaultUrl)
  );
}

/** XYZ template (`{z}/{x}/{y}`) or "" when no raster basemap is configured. */
export const rasterTileUrlTemplate = resolveRasterTileUrl(buildEnv);

export const formatRasterTileUrl = (
  template: string,
  z: number,
  x: number,
  y: number
) =>
  template
    .replace("{z}", String(z))
    .replace("{x}", String(x))
    .replace("{y}", String(y));

// ---------------------------------------------------------------------------
// DuckDB extensions
// ---------------------------------------------------------------------------

/**
 * Custom DuckDB extension repository (spatial/httpfs). Unset keeps DuckDB's
 * default (extensions.duckdb.org); on-prem defaults to a same-origin mirror.
 */
export function resolveDuckDBExtensionRepository(
  env: ThirdPartyEnv,
  origin?: string
): string | undefined {
  const configured = readString(env, "VITE_DUCKDB_EXTENSION_REPOSITORY");
  const value =
    configured ?? (isOnPremEnv(env) ? "/duckdb-extensions" : undefined);
  if (!value) return undefined;
  if (!origin || /^[a-z][a-z0-9+.-]*:/i.test(value)) return value;
  return new URL(value, origin).toString().replace(/\/$/, "");
}

// ---------------------------------------------------------------------------
// PostHog
// ---------------------------------------------------------------------------

export type PostHogPrivacyConfig = {
  autocapture: boolean;
  sessionRecording: boolean;
  identifyEmail: boolean;
};

export function resolvePostHogPrivacyConfig(
  env: ThirdPartyEnv
): PostHogPrivacyConfig {
  return {
    autocapture: readFlag(env, "VITE_POSTHOG_AUTOCAPTURE", false),
    sessionRecording: readFlag(env, "VITE_POSTHOG_SESSION_RECORDING", false),
    identifyEmail: readFlag(env, "VITE_POSTHOG_IDENTIFY_EMAIL", false),
  };
}
